import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { brand } = useWorkspace();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const sendForgot = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email first");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setExpired(false);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      nav(["admin", "super_admin"].includes(u.role) ? "/admin" : "/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      if (err.response?.status === 403 && /trial expired/i.test(msg)) {
        setExpired(true);
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          className="absolute inset-0 w-full h-full object-cover"
          src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1200&q=80"
          alt="Mentoring conversation"
        />
        <div className="absolute inset-0" style={{backgroundColor:'color-mix(in srgb, var(--brand-primary) 40%, transparent)'}} />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="label-eyebrow text-white/70 mb-3">{brand.name}</div>
          <div className="font-display text-4xl leading-tight max-w-md">
            The onboarding your new hires deserve.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 bg-[color:var(--brand-bg)]">
        <div className="w-full max-w-md">
          <Link to="/" className="label-eyebrow" data-testid="back-home">← Back home</Link>
          <h1 className="font-display text-4xl mt-6 mb-2">Sign in</h1>
          <p className="text-neutral-500 mb-10">Continue your onboarding journey.</p>

          {expired && (
            <div className="mb-6 border-2 border-[#8B2E2E] bg-[#8B2E2E]/5 p-5" data-testid="trial-expired-banner">
              <div className="label-eyebrow text-[#8B2E2E] mb-1">Trial expired</div>
              <div className="font-display text-lg leading-tight">Your 14-day trial has ended.</div>
              <p className="text-sm text-neutral-600 mt-2">Your workspace and data are preserved. Contact us to upgrade and keep going.</p>
              <a href="mailto:info@emmaus-partners.com?subject=Upgrade%20my%20workspace" className="btn-primary text-sm inline-flex mt-3" data-testid="trial-upgrade-btn">Contact us to upgrade</a>
            </div>
          )}

          {forgot ? (
            forgotSent ? (
              <div className="border-2 border-[color:var(--brand-primary)] bg-white p-6" data-testid="forgot-sent">
                <div className="label-eyebrow text-[var(--brand-primary)] mb-2">Request sent</div>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  If that account exists, your admin has been notified. Once they approve,
                  you'll receive a <strong>temporary password</strong> to sign in with — you'll set
                  a new one right after.
                </p>
                <button className="btn-ghost text-sm mt-4" onClick={() => { setForgot(false); setForgotSent(false); }} data-testid="forgot-back">← Back to sign in</button>
              </div>
            ) : (
              <form onSubmit={sendForgot} className="space-y-5" data-testid="forgot-form">
                <div>
                  <label className="label-eyebrow block mb-2">Email</label>
                  <input
                    type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                    data-testid="forgot-email"
                    className="w-full bg-transparent border hairline px-4 py-3 focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-1 focus:ring-[color:var(--brand-primary)]"
                  />
                </div>
                <p className="text-sm text-neutral-500">Your admin will approve the request and send you a temporary password.</p>
                <button disabled={busy} type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-hover)] text-white px-7 py-3.5 font-semibold rounded-sm transition-colors disabled:opacity-60" data-testid="forgot-submit">
                  {busy ? "Sending…" : "Request password reset"}
                </button>
                <button type="button" className="w-full btn-ghost text-sm" onClick={() => setForgot(false)} data-testid="forgot-cancel">← Back to sign in</button>
              </form>
            )
          ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full bg-transparent border hairline px-4 py-3 focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-1 focus:ring-[color:var(--brand-primary)]"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <input
                type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full bg-transparent border hairline px-4 py-3 focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-1 focus:ring-[color:var(--brand-primary)]"
              />
            </div>
            <button disabled={busy} type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-hover)] text-white px-7 py-3.5 font-semibold rounded-sm transition-colors disabled:opacity-60" data-testid="login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <button type="button" className="w-full text-sm text-neutral-500 link-underline" onClick={() => setForgot(true)} data-testid="forgot-password-link">
              Forgot password?
            </button>
          </form>
          )}

          <div className="mt-8 text-sm text-neutral-500">
            New here? <Link to="/register" className="text-[var(--brand-primary)] link-underline" data-testid="link-register">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
